/**
 * This module is responsible for storing all of the user tasks in the entire app.
 * A user may be a part of many groups, and those groups will have their own tasks
 * in the database. The role of this module is to unify all of those tasks into a
 * single place where the app can access all of them at once.
 */

import { first as rxjs_op_first } from "rxjs/operators";
import Vue from "vue";
import firebase from "firebase/app";
import { cleanupObj, minutesToLocaleTime, pathHead } from "../helpers";
import { cache } from "./helpers/actions.store";
import { access } from "./helpers/getters.store";

import * as db from "@/db/constants";
import { week_days } from "@/mixins/days.mixin";

import {
  CACHED,
  STORED,
  ACCESS,
  G_USER_GROUPS,
  G_USER_ID,
  G_DEFAULT_GROUP,

  SORTED,
  FILTERED,
  GROUP_ACTION_LISTENERS,
  IS_LISTENING_TO_GROUP_ACTION,
  IS_LISTENING_TO_NOTIFICATIONS,
  ALL_TASKS,
  IS_WATCHING,
} from "./helpers/getter.type";

import {
  PUSH_TASK,
  SET_FETCH_TIMESTAMP,

  UPDATE_TASKS,
  DETACH_LISTENER,
  DETACH_ALL_LISTENERS,
  UPDATE_GROUP_TASKS_SNAPSHOT,
  INSERT_GROUP_ACTIONS_LISTENER,
  UPDATE_GROUP_TASK_NOTIFICATIONS_SNAPSHOT,
  SET_NOTIFICATIONS_LISTENER,
  DETACH_NOTIFICATIONS_LISTENER,
} from "./helpers/mutation.type";

import {
  GET,
  CACHE,
  WATCH,
  FETCH,
  G_ATTATCH_LISTENER,

  WATCH_GROUPS_ACTIONS,
  WATCH_GROUP_ACTION,
  WATCH_NOTIFICATIONS,
  FETCH_GROUPS_ACTIONS,
  FETCH_GROUP_ACTION,
  FETCH_TASK_NOTIFICATIONS,
  USER_TASK_CREATE,
  USER_TASK_DELETE,
  TASK_UPDATE,
  UPDATE_NOTIFICATION,
  FETCH_NOTIFICATIONS,
} from "./helpers/action.type";
import { firestore } from "@/firebase";

export const getters = {
  [CACHED]: state => {
    const tasks = {};
    for (const task in state.tasks) {
      const curr_task = state.tasks[task];
      tasks[task] = {
        group_id: curr_task.group_id,
        ...curr_task.task,
        ...curr_task.notification,
      };
    }
    return { tasks };
  },
  [ALL_TASKS]: (_state, getters) => getters[CACHED].tasks,
  [SORTED]: state => {
    // TODO: Implement
    console.warn("implement sorting of tasks");
    return [];
  },
  [FILTERED]: state => (filterPath) => {
    console.warn("implement filtering of tasks");
    return [];
  },
  [IS_WATCHING]: (state, _getters, _rootState, rootGetters) => {
    return rootGetters[G_USER_GROUPS]
      .every(group => group.id in state.group_action_listeners) &&
      state.notifications_listener;
  },
  [STORED]: (_state, getters) => getters[IS_WATCHING],
  [ACCESS]: access,
  [GROUP_ACTION_LISTENERS]: state => state.group_action_listeners,
  [IS_LISTENING_TO_GROUP_ACTION]: state => group_id => group_id in state.group_action_listeners,
  [IS_LISTENING_TO_NOTIFICATIONS]: state => !!state.notifications_listener,
};


export const mutations = {
  /**
   * Pushes a new task to the store.
   * @param {TaskStore} state 
   * @param {Task} task 
   */
  [PUSH_TASK]: (state, task) => {
    state.tasks.push(task);
  },

  /**
   * Overwrites the current tasks with new ones.
   * @param {TaskStore} state
   * @param {Task[]} tasks
   */
  [UPDATE_TASKS]: (state, tasks) => {
    Vue.set(state, "tasks", tasks);
  },

  /**
   * Takes a raw firestore **collection snapshot**, transforms and inserts into the state.
   * @param {TaskStore} state 
   * @param {*} snapshot 
   */
  [UPDATE_GROUP_TASKS_SNAPSHOT]: (state, { snapshot, group_id }) => {
    // Figure out which tasks have been deleted.
    const updated_ids = new Set(snapshot.docs.map(doc => doc.id));
    for (const id of Object.keys(state.tasks))
      if (!updated_ids.has(id))
        Vue.delete(state.tasks, id);

    snapshot.forEach((doc) => {
      /**
       * @type {Task}
       */
      const data = doc.data();
      const id = doc.id;

      if (!state.tasks[id]) {
        Vue.set(state.tasks, id, {
          group_id: group_id,
        });
      }
      // TODO: Maybe some additional processing will be needed.
      Vue.set(state.tasks[id], "task", {
        id,
        ...data,
      });
    });
  },

  [UPDATE_GROUP_TASK_NOTIFICATIONS_SNAPSHOT]: (state, { snapshot }) => {
    snapshot.forEach((doc) => {
      const data = doc.data();
      const [
        _groups,
        group_id,
        _tasks,
        task_id,
        _notifications,
        _notification_id,
      ] = doc.ref.path.split('/');

      if (!state.tasks[task_id]) {
        Vue.set(state.tasks, task_id, {
          group_id: group_id,
        });
      }
      // TODO: There may be multiple notifications for a single task.
      //       However this only handles the case with one notification
      Vue.set(state.tasks[task_id], "notification", {
        notification_id: doc.ref.id,
        ...data
      });
    });
  },

  [SET_FETCH_TIMESTAMP]: (state, timestamp) => {
    state.fetch_timestamp = timestamp ?? new Date().getTime();
  },

  /**
   * Inserts a group action listener to the inner storage.
   * @param {TaskStore} state 
   * @param {Object} payload
   * @param {String} payload.group_id
   * @param {*} payload.listener
   */
  [INSERT_GROUP_ACTIONS_LISTENER]: (state, { group_id, listener }) => {
    if (!(group_id in state.group_action_listeners)) {
      Vue.set(state.group_action_listeners, group_id, listener);
    } else {
      console.error(`${INSERT_GROUP_ACTIONS_LISTENER} mutation failed because`,
                    "tried to insert a group action listener already present.");
    }
  },

  [SET_NOTIFICATIONS_LISTENER]: (state, listener) => {
    if (!state.notifications_listener) {
      Vue.set(state, "notifications_listener", listener);
    } else {
      console.error(`${SET_NOTIFICATIONS_LISTENER} mutation failed because tried`,
                    "to set a notification listener while one was alread present.");
    }
  },

  [DETACH_NOTIFICATIONS_LISTENER]: (state) => {
    if (state.notifications_listener)
      state.notifications_listener.complete();
    
    Vue.set(state, "notifications_listener", null);
  },

  /**
   * Finds the listener to a group, detaches it, and removes it.
   * @param {TaskStore} state 
   * @param {String} group_id
   */
  [DETACH_LISTENER]: (state, group_id) => {
    const listener = state.group_action_listeners[group_id];
    if (listener) {
      listener.complete();
      delete state.group_action_listeners[group_id];
    } else {
      console.error("Tried to detach a listener that was not attached.");
    }
  },
  [DETACH_ALL_LISTENERS]: (state) => {
    for (const listener of Object.values(state.group_action_listeners)) {
      listener.complete();
    }
    state.group_action_listeners = {};
  },
};

export const actions = {
  [GET]: async ({ dispatch }, payload) => {
    await dispatch(CACHE, payload?.options);
    if (pathHead(payload?.path))
      throw new Error("Path not supported for this operation");
    else
      return getters.cached;
  },
  // Currently if some group is not beeing watched, it will detatch all other listeners
  // and reatatch them with the group that was not watched.
  // TODO: Optimize this process.
  [CACHE]: cache,
  [WATCH]: async ({ commit, dispatch }) => {
    try {
      await Promise.all([
        dispatch(WATCH_GROUPS_ACTIONS),
        dispatch(WATCH_NOTIFICATIONS),
      ]);
      commit(SET_FETCH_TIMESTAMP);
    } catch (error) {
      console.error(error);
      throw Error("An error occurred while watching the context doc and/or the items of the context.");
    }
  },
  [FETCH]: async ({ commit, dispatch }) => {
    try {
      await dispatch(FETCH_GROUPS_ACTIONS);
      commit(SET_FETCH_TIMESTAMP);
    } catch (error) {
      console.error(error);
      throw new Error(`${FETCH} action failed.`);
    }
  },
  [WATCH_GROUPS_ACTIONS]: async ({ rootGetters, dispatch }) => {
    const all_promises = [];
    for (const group of rootGetters[G_USER_GROUPS]) {
      all_promises.push(dispatch(WATCH_GROUP_ACTION, group));
    }

    try {
      // Awaits for all promises to resolve. This allows paralel processing.
      await Promise.all(all_promises);
    } catch (error) {
      console.error(error);
      throw new Error(`${WATCH_GROUPS_ACTIONS} action failed.`);
    }
  },
  [WATCH_GROUP_ACTION]: async ({ rootGetters, getters, commit, dispatch }, group) => {
    if (getters[IS_LISTENING_TO_GROUP_ACTION](group.id)) {
      commit(DETACH_LISTENER, group.id);
    }

    try {
      const ref = group
        .collection(db.TASKS_COLLECTION)
        .where(db.TASK_RESPONSIBLE, 'array-contains', rootGetters[G_USER_ID]);

      // Attaches a document listener to the store.
      const subject = await dispatch(G_ATTATCH_LISTENER, {
        ref,
        mutation: snapshot => {
          commit(UPDATE_GROUP_TASKS_SNAPSHOT, {
            snapshot,
            group_id: group.id,
          });
        },
      }, { root: true });

      commit(INSERT_GROUP_ACTIONS_LISTENER, {
        group_id: group.id,
        listener: subject,
      });

      return await subject.pipe(rxjs_op_first()).toPromise();
    } catch (error) {
      console.error(error);
      throw new Error(`${WATCH_GROUP_ACTION} action failed.`);
    }
  },
  [WATCH_NOTIFICATIONS]: async ({ getters, rootGetters, commit, dispatch }) => {
    if (getters[IS_LISTENING_TO_NOTIFICATIONS]) {
      commit(DETACH_NOTIFICATIONS_LISTENER);
    }

    const uid = rootGetters[G_USER_ID];
    try {
      const ref = firestore
        .collectionGroup("notifications")
        .where(db.NOTIFICATION_OWNER, "==", uid);

      // Attaches a document listener to the store.
      const subject = await dispatch(G_ATTATCH_LISTENER, {
        ref,
        mutation: snapshot => {
          commit(UPDATE_GROUP_TASK_NOTIFICATIONS_SNAPSHOT, { snapshot });
        },
      }, { root: true });

      commit(SET_NOTIFICATIONS_LISTENER, subject);

      return await subject.pipe(rxjs_op_first()).toPromise();
    } catch (error) {
      console.error(error);
      throw new Error(`${WATCH_NOTIFICATIONS} action failed.`);
    }
  },
  [FETCH_GROUPS_ACTIONS]: async ({ rootGetters, commit, dispatch }) => {
    console.trace("FETCH GROUP ACTIONS");
    // Clear all actions.
    commit(UPDATE_TASKS, {});

    const all_promises = [];
    for (const group of rootGetters[G_USER_GROUPS]) {
      all_promises.push(dispatch(FETCH_GROUP_ACTION, group));
    }

    try {
      // Awaits for all promises to resolve. This allows paralel processing.
      await Promise.all(all_promises);
    } catch (error) {
      console.error(error);
      throw new Error(`${FETCH_GROUPS_ACTIONS} action failed.`);
    }
  },
  [FETCH_GROUP_ACTION]: async ({ rootGetters, commit, dispatch }, group) => {
    const uid = rootGetters[G_USER_ID];
    try {
      const snapshot = await group.collection(db.TASKS_COLLECTION)
        .where(db.TASK_RESPONSIBLE, 'array-contains', uid)
        .get();
      
      commit(UPDATE_GROUP_TASKS_SNAPSHOT, { snapshot, group_id: group.id });

      await Promise.all(
        snapshot.docs
          .map(async doc =>
            await dispatch(FETCH_TASK_NOTIFICATIONS, doc.ref)
          )
      );

      return snapshot;
    } catch (error) {
      console.error(error);
      throw new Error(`${FETCH_GROUP_ACTION} action failed`);
    }
  },

  [FETCH_NOTIFICATIONS]: async ({ rootGetters, commit }) => {
      const uid = rootGetters[G_USER_ID];
      try {
        const ref = firestore
          .collectionGroup("notifications")
          .where(db.NOTIFICATION_OWNER, "==", uid);
        
        const snapshot = await ref.get();
        commit(UPDATE_GROUP_TASK_NOTIFICATIONS_SNAPSHOT, { snapshot })
        return snapshot;
      } catch (error) {
        console.error(error);
        throw new Error(`${FETCH_NOTIFICATIONS} action failed.`);
      }
  },

  [FETCH_TASK_NOTIFICATIONS]: async ({ rootGetters, commit }, task) => {
    const uid = rootGetters[G_USER_ID];
    try {
      // Get all of the notifications and merge them with their corresponding task.
      const snapshot = await task.collection(db.NOTIFICATIONS_COLLECTION)
        .where(db.NOTIFICATION_OWNER, "==", uid)
        .get();

      commit(UPDATE_GROUP_TASK_NOTIFICATIONS_SNAPSHOT, { snapshot });
      return snapshot;
    } catch (error) {
      console.error(error);
      throw new Error(`${FETCH_TASK_NOTIFICATIONS} action failed`);
    }
  },

  /**
   * Takes a task from the input form and sends it to the database. The task will
   * be registered to the specified group.
   * Will also create a notification collection under the task doc and insert one
   * document with the configured notification inside it. The notification object
   * will hold part of the form data.
   * 
   * @param {*}         action_param 
   * @param {Object}    payload 
   * @param {Object}    payload.task
   * @param {String}    payload.task.title
   * @param {String}    payload.task.description
   * @param {String=}   payload.task.color
   * @param {Object=}   payload.task.notification
   * @param {Object=}   payload.task.email
   * @param {number}    payload.task.week_days
   * @param {String}    payload.task.due_date
   * @param {String}    payload.task.end_date
   * @param {Number}    payload.task.time
   * @param {String[]}  payload.task.associations
   * @param {String}    payload.task.is_periodic 
   * @param {Array<{
   *  text:  String,
   *  color: String,
   *  id:    String?,
   * }>}                payload.task.tags
   * @param {Group}     payload.group
   * @returns {*} A reference to the task doc.
   */
  [USER_TASK_CREATE]: async ({ rootGetters }, { task, group }) => {
    const uid = rootGetters[G_USER_ID];
    try {
      const ref = group.collection(db.TASKS_COLLECTION).doc();

      await ref.set(cleanupObj({
          created_at:   firebase.firestore.Timestamp.now(),
          created_by:   uid,
          title:        task.title,
          description:  task.description,
          color:        task.color,
          associations: task.associations,
          tags:         task.tags,
          done:         false,
          // TODO: This should not be hard coded!!!
          [db.TASK_RESPONSIBLE]: [uid],
        }));
      
      // This function will also create a notification. For the MVP there can only
      // be one notification. But in the future this should be turned into a separate
      // action that handles n notifications.
      const notification_ref = ref.collection(db.NOTIFICATIONS_COLLECTION).doc();

      if (task.push_notify || task.email_notify) {
        await notification_ref.set(cleanupObj({
          is_periodic:     task.is_periodic,
          date:            task.due_date,
          time:            task.time,
          week_days:       task.week_days,
          email_notify:    task.email_notify ?? false,
          push_notify:     task.push_notify ?? false,
          // TODO: This should not be hard coded!!
          timezone_offset: 180,
          push_payload:    task.notification,
          email_payload:   task.email,
          owner:           uid,
        }));
      }

      return ref;
    } catch (error) {
      console.error(error)
      throw new Error(`${USER_TASK_CREATE} action failed`);
    }
  },
  [TASK_UPDATE]: async (_, { task: _t, group: _g }) => {
    throw new Error(`${TASK_UPDATE} is not yet implemented.`);
    /*
    if (!task.id)
      throw new Error(`${TASK_UPDATE} action failed because no task.id was found`);

    try {
      const ref = group.collection(db.TASKS_COLLECTION).doc(task.id);

      await ref.update(cleanupObj({
        week_days: task.week_days,
        done:      task.done,
      }));
    } catch (error) {
      console.error(error)
      throw new Error(`${TASK_UPDATE} action failed`);
    }
    */
  },
  // NOTE: Only works with default group.
  // TODO: Make this generic for any groups.
  [USER_TASK_DELETE]: async ({ rootGetters }, task_id) => {
    const group = rootGetters[G_DEFAULT_GROUP];
    const ref = group.collection(db.TASKS_COLLECTION).doc(task_id);
    try {
      await ref.delete();
      // return await dispatch(FETCH_GROUP_ACTION, group);
    } catch (error) {
      console.error(error)
      throw new Error(`${USER_TASK_CREATE} action failed.`);
    }
  },
  /**
   * 
   * @param {*}       action_param
   * @param {Object}  task 
   * @param {String}  task.id - The id of the task.
   * @param {Number=} task.week_days - The updated week_days.
   */
  // TODO: This should really also take a `notification_id` argument, but for now
  //       we only care about a single notification per task
  [UPDATE_NOTIFICATION]: async ({ rootGetters, getters }, task) => {
    const group = rootGetters[G_DEFAULT_GROUP];
    const notification_id = getters[ALL_TASKS][task.id]?.notification_id;

    if (!notification_id) {
      throw new Error(`${UPDATE_NOTIFICATION} action failed the given task has no notification id.`);
    }

    const ref = group
      .collection(db.TASKS_COLLECTION)
      .doc(task.id)
      .collection(db.NOTIFICATIONS_COLLECTION)
      .doc(notification_id);
    
    const obj = cleanupObj({ week_days: task.week_days });

    if (Object.keys(obj).length == 0) {
      console.warn(`Called ${UPDATE_NOTIFICATION} without any updated data.`);
      return;
    }

    try {
      await ref.update(obj);
    } catch (error) {
      console.error(error);
      throw new Error(`${UPDATE_NOTIFICATION} action failed.`);
    }
  },
};

/**
 * The object of this store
 * @typedef  {Object}              TaskStore
 * @property {Map<String, Task>}   tasks
 * @property {String}              moduleName
 * @property {Map<String, Lister>} group_action_listeners
 * @property {Timestamp}           fetch_timestamp
 */

/**
 * A single task object
 * @typedef  {Object}        Task
 * @property {String}        id            Unique task identifier
 * @property {Timestamp}     created_at
 * @property {String}        created_by
 * @property {String}        title
 * @property {String}        description
 * @property {String=}       color
 * @property {boolean}       done          Whether the action can be archived.
 * @property {Object=}       notification  The notification object passed to FCM.
 * @property {boolean}       should_notify Whether or not this action should trigger a notification.
 * @property {boolean}       is_periodic
 * @property {number}        week_days
 * @property {Date=}         due_date      The date that the task is due. Should not include time.
 * @property {Date=}         end_date      The end date for periodic tasks.
 * @property {Time=}         time          When the action is going to occur.
 * @property {Path[]}        associations  What group, context, category and item this task is attached to.
 * @property {String[]=}     tags
 */

/** 
 * A day of the week.
 * @typedef {"mon" | "tue" | "wen" | "thu" | "fri" | "sat" | "sun"} WeekDay
 */

/**
 * Generates a state with the given module name.
 * @param {String} moduleName 
 * @returns {TaskStore}
 */
export const state = (moduleName) => ({
  moduleName,
  tasks: {},
  group_action_listeners: {},
  notifications_listener: null,
  fetch_timestamp: null,
});

/**
 * Constructor for the tasks store object.
 * @param {String} moduleName
 * @returns {TaskStore}
 */
export default (moduleName) => ({
  namespaced: true,
  state: state(moduleName),
  getters,
  mutations,
  actions,
  is_stored: false,
});
